import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DASHBOARD } from '@/constants/testIds';
import Navigation from '@/components/Navigation';
import TwoFASettingsCard from '@/components/TwoFASettingsCard';
import ProgressChecklist from '@/components/ProgressChecklist';
import {
  Building2,
  FileText,
  Calendar,
  CreditCard,
  AlertCircle,
  ChevronDown,
  Plus,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  Clock,
  Hourglass
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ENQUIRY_TYPES = [
  { value: 'building_defects', label: 'Building Defects' },
  { value: 'specialist_services', label: 'Specialist Services' },
  { value: 'construction_sequencing', label: 'Construction Sequencing' },
  { value: 'cost_management', label: 'Cost Management' },
  { value: 'diy_projects', label: 'DIY Projects' },
  { value: 'trade_works_admin', label: 'Trade Works & Administration', comingSoon: true }
];

const UPCOMING_STAGES = new Set(['intake_submitted', 'deposit_paid']);

// Add N business days (Mon–Fri) to a base ISO datetime string. Used to estimate
// when the team will respond to an enquiry and when the report should land.
function addBusinessDays(baseIso, days) {
  if (!baseIso) return null;
  const d = new Date(baseIso);
  let added = 0;
  while (added < days) {
    d.setUTCDate(d.getUTCDate() + 1);
    const wd = d.getUTCDay();
    if (wd !== 0 && wd !== 6) added += 1;
  }
  return d;
}

function formatShortDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
}

export default function Dashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const enquiryRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (enquiryRef.current && !enquiryRef.current.contains(event.target)) {
        setEnquiryOpen(false);
      }
    };
    if (enquiryOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [enquiryOpen]);

  const handleEnquirySelect = (enquiryType) => {
    setEnquiryOpen(false);
    navigate(
      `/bookings?enquiry=${encodeURIComponent(enquiryType.value)}&label=${encodeURIComponent(enquiryType.label)}`
    );
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      // Cookie carries the session post-XSS-hardening migration. Keep the
      // Bearer header too — backend's get_current_user accepts either.
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const bookingsRes = await axios.get(`${API}/bookings`, { headers });
      setBookings(bookingsRes.data);
    } catch (error) {
      // 401 here typically means /auth/me hasn't resolved yet on a hard
      // refresh — the parent App is still bootstrapping. The user state will
      // flip and this effect will re-run, so silently no-op.
      if (error?.response?.status !== 401) {
        console.error('Failed to fetch dashboard data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Wait until App.js has finished bootstrapping the user (avoids a spurious
    // 401 on the very first render in StrictMode dev builds).
    if (!user) return;
    fetchDashboardData();
  }, [user, fetchDashboardData]);

  // Stat values derived from bookings only (Projects section was removed)
  const stats = useMemo(() => {
    const upcoming = bookings.filter((b) => UPCOMING_STAGES.has(b.stage)).length;
    const reports = bookings.filter((b) => !!b.report_pdf_path).length;
    const unpaid = bookings.filter((b) => b.payment_status !== 'paid').length;
    return {
      enquiries: bookings.length,
      upcoming,
      reports,
      payments: unpaid
    };
  }, [bookings]);

  // Contextual "next step" hint
  const nextStep = useMemo(() => {
    if (bookings.length === 0) {
      return {
        title: 'Start your first Build Enquiry',
        body: 'Submit a pre-assessment intake to engage the All Build Consult team.',
        cta: 'Start Pre-Assessment',
        href: '/bookings'
      };
    }
    const awaitingPayment = bookings.find((b) => b.payment_status !== 'paid');
    if (awaitingPayment) {
      return {
        title: 'Invoice awaiting payment',
        body: `Pay your deposit for ${awaitingPayment.service_type} to start the review.`,
        cta: 'Go to Payments',
        href: '/payments'
      };
    }
    const newReport = bookings.find((b) => b.report_pdf_path && b.payment_status === 'paid');
    if (newReport) {
      return {
        title: 'Your report is ready',
        body: 'Download the latest report from the Reports tab.',
        cta: 'View Reports',
        href: '/reports'
      };
    }
    return {
      title: "You're all caught up",
      body: 'The All Build Consult team is processing your active bookings.',
      cta: 'View Bookings',
      href: '/bookings'
    };
  }, [bookings]);

  const statTiles = [
    {
      key: 'enquiries',
      label: 'Build Enquiry',
      value: stats.enquiries,
      icon: Building2,
      accent: 'bg-black text-white',
      onClick: () => navigate('/bookings')
    },
    {
      key: 'upcoming',
      label: 'Review & Response',
      value: stats.upcoming,
      icon: Calendar,
      accent: 'bg-blue-600 text-white',
      onClick: () => navigate('/bookings')
    },
    {
      key: 'reports',
      label: 'Reports Received',
      value: stats.reports,
      icon: FileText,
      accent: 'bg-emerald-600 text-white',
      onClick: () => navigate('/reports')
    },
    {
      key: 'payments',
      label: 'Payments',
      value: stats.payments,
      icon: CreditCard,
      accent: 'bg-amber-600 text-white',
      onClick: () => navigate('/payments')
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
            <p className="mt-4 text-sm text-slate-600">Loading dashboard…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <div data-testid={DASHBOARD.container} className="max-w-7xl mx-auto px-6 py-8 mt-16">
        {/* Header + Quick Actions row */}
        <div className="flex items-start justify-between gap-6 mb-6 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-black mb-1" style={{ fontFamily: 'Outfit' }}>
              Welcome back, {user.name}
            </h1>
            <p className="text-slate-600">Summary &amp; status of all your build requests.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div ref={enquiryRef} className="relative">
              <button
                data-testid={DASHBOARD.createProjectButton}
                onClick={() => setEnquiryOpen(!enquiryOpen)}
                className="px-4 py-2.5 bg-black text-white rounded-sm hover:bg-slate-800 transition-all text-sm font-medium inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Start Build Enquiry
                <ChevronDown className={`w-4 h-4 transition-transform ${enquiryOpen ? 'rotate-180' : ''}`} />
              </button>
              {enquiryOpen && (
                <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-slate-300 rounded-sm shadow-lg z-10 overflow-hidden">
                  {ENQUIRY_TYPES.map((type) => (
                    <button
                      key={type.value}
                      data-testid={`enquiry-option-${type.value}`}
                      onClick={() => !type.comingSoon && handleEnquirySelect(type)}
                      disabled={!!type.comingSoon}
                      className={`w-full py-3 px-4 transition-all text-left text-sm border-b border-slate-100 last:border-0 flex items-center justify-between gap-2 ${
                        type.comingSoon
                          ? 'bg-slate-50 text-slate-400 cursor-not-allowed'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <span>{type.label}</span>
                      {type.comingSoon && (
                        <span className="px-1.5 py-0.5 bg-red-600 text-white text-[9px] font-bold uppercase tracking-wider rounded-sm">
                          Coming Soon
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => navigate('/bookings')}
              className="px-4 py-2.5 border border-slate-300 rounded-sm hover:bg-white transition-all text-sm font-medium inline-flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              Book Pre-Assessment
            </button>
            <button
              onClick={() => navigate('/services')}
              className="px-4 py-2.5 border border-slate-300 rounded-sm hover:bg-white transition-all text-sm font-medium inline-flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Browse Services
            </button>
          </div>
        </div>

        {/* Contextual next-step callout */}
        <div
          data-testid="dashboard-next-step"
          className="grid-border bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200 p-5 mb-6 flex items-start justify-between gap-4 flex-wrap"
        >
          <div className="flex items-start gap-3 max-w-2xl">
            <div className="w-10 h-10 bg-white border border-blue-200 rounded-sm flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-black" style={{ fontFamily: 'Outfit' }}>
                {nextStep.title}
              </h3>
              <p className="text-sm text-slate-700">{nextStep.body}</p>
            </div>
          </div>
          <button
            data-testid="dashboard-next-step-cta"
            onClick={() => navigate(nextStep.href)}
            className="px-4 py-2 bg-black text-white rounded-sm hover:bg-slate-800 transition-all text-sm font-medium inline-flex items-center gap-2"
          >
            {nextStep.cta}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Your Progress — 6-step workflow visibility (same component used on Consultants page) */}
        <div className="mb-6">
          <ProgressChecklist bookings={bookings} variant="card" />
        </div>

        {/* Clickable stat tiles */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statTiles.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`dashboard-stat-${t.key}`}
                onClick={t.onClick}
                className="grid-border bg-white p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all group"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 ${t.accent} rounded-sm flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-black group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="text-3xl font-bold text-black" style={{ fontFamily: 'Outfit' }}>
                  {t.value}
                </div>
                <p className="text-xs text-slate-600 uppercase tracking-wide mt-0.5">{t.label}</p>
              </button>
            );
          })}
        </div>

        {/* === Booking Register === */}
        <div data-testid="dashboard-booking-register" className="grid-border bg-white overflow-hidden mb-6">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-black" style={{ fontFamily: 'Outfit' }}>
              Register of Requests &amp; Bookings
            </h2>
            <p className="text-sm text-slate-600">
              Every active build enquiry with response, report turnaround and payment status.
            </p>
          </div>

          {bookings.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <AlertCircle className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p>No requests yet. Use <strong>Start Build Enquiry</strong> above to add one.</p>
            </div>
          ) : (
            <>
              {/* Header row (desktop only) */}
              <div className="hidden lg:grid grid-cols-12 gap-4 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wide font-semibold text-slate-500">
                <div className="col-span-3">Build Enquiry</div>
                <div className="col-span-3">Review &amp; Response</div>
                <div className="col-span-3">Reports Received</div>
                <div className="col-span-3">Payments</div>
              </div>

              <ul className="divide-y divide-slate-100">
                {bookings.map((b) => {
                  const isPaid = b.payment_status === 'paid';
                  const isTradeWorks = (b.service_type || '').toLowerCase().includes('trade works');

                  // Estimated initial review = the scheduled preferred_slot
                  const reviewDate = b.preferred_slot
                    ? new Date(b.preferred_slot)
                    : b.preferred_date
                      ? new Date(b.preferred_date)
                      : null;
                  // Estimated report turnaround = 3–5 business days from the inspection
                  const reportEta = reviewDate ? addBusinessDays(reviewDate.toISOString(), 5) : null;
                  const hasReport = !!b.report_pdf_path;

                  return (
                    <li
                      key={b.id}
                      data-testid="dashboard-register-row"
                      className="grid grid-cols-1 lg:grid-cols-12 gap-4 px-4 py-4 hover:bg-slate-50/60 transition-all"
                    >
                      {/* === Col 1: Build Enquiry === */}
                      <button
                        data-testid={`register-enquiry-${b.id}`}
                        onClick={() => navigate('/bookings')}
                        className="col-span-3 text-left group"
                      >
                        <div className="lg:hidden text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
                          Build Enquiry
                        </div>
                        <div className="text-sm font-semibold text-black group-hover:text-blue-700 inline-flex items-center gap-1.5 flex-wrap">
                          <span>{b.service_type}</span>
                          <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                          {b.property_address || 'No property address'}
                        </div>
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                          <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 text-[9px] uppercase font-semibold tracking-wider rounded-sm">
                            {b.urgency || 'medium'}
                          </span>
                          {isTradeWorks && (
                            <span className="px-1.5 py-0.5 bg-red-600 text-white text-[9px] font-bold uppercase tracking-wider rounded-sm">
                              Coming Soon
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400 ml-1">
                            {new Date(b.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </button>

                      {/* === Col 2: Review & Response === */}
                      <div className="col-span-3">
                        <div className="lg:hidden text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
                          Review &amp; Response
                        </div>
                        <div className="text-xs font-medium text-blue-800 inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Response by All Build Consult
                        </div>
                        <div className="text-sm text-black font-medium mt-0.5">
                          {reviewDate ? `Initial review: ${formatShortDate(reviewDate)}` : 'Scheduling…'}
                        </div>
                        {reviewDate && (
                          <div className="text-[11px] text-slate-500">
                            {reviewDate.toLocaleTimeString('en-AU', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                              timeZone: 'Australia/Brisbane'
                            })} AEST
                          </div>
                        )}
                      </div>

                      {/* === Col 3: Reports Received === */}
                      <div className="col-span-3">
                        <div className="lg:hidden text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
                          Reports Received
                        </div>
                        {hasReport ? (
                          <>
                            <div className="text-xs font-medium text-emerald-700 inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Report ready
                            </div>
                            <button
                              data-testid={`register-report-${b.id}`}
                              onClick={() => navigate('/reports')}
                              className="text-sm text-black font-medium hover:text-blue-700 inline-flex items-center gap-1 mt-0.5"
                            >
                              Open Reports tab
                              <ArrowRight className="w-3 h-3" />
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="text-xs font-medium text-amber-700 inline-flex items-center gap-1">
                              <Hourglass className="w-3 h-3" />
                              Estimated turnaround
                            </div>
                            <div className="text-sm text-black font-medium mt-0.5">
                              {reportEta ? `By ${formatShortDate(reportEta)}` : '3–5 business days'}
                            </div>
                            <button
                              onClick={() => navigate('/reports')}
                              className="text-[11px] text-blue-700 hover:text-blue-900 mt-0.5"
                            >
                              Track in Reports tab →
                            </button>
                          </>
                        )}
                      </div>

                      {/* === Col 4: Payments === */}
                      <button
                        data-testid={`register-payment-${b.id}`}
                        onClick={() => navigate('/payments')}
                        className="col-span-3 text-left group"
                      >
                        <div className="lg:hidden text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
                          Payments
                        </div>
                        <div className="text-sm font-semibold text-black inline-flex items-center gap-1 group-hover:text-blue-700">
                          A${(b.budget || 0).toFixed(2)}
                          <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="mt-1">
                          {isPaid ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-[10px] uppercase font-semibold tracking-wider rounded-sm">
                              <CheckCircle2 className="w-3 h-3" />
                              Paid
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] uppercase font-semibold tracking-wider rounded-sm">
                              <Hourglass className="w-3 h-3" />
                              Outstanding
                            </span>
                          )}
                        </div>
                        {isPaid && b.paid_at && (
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            on {new Date(b.paid_at).toLocaleDateString()}
                          </div>
                        )}
                        {!isPaid && (
                          <div className="text-[11px] text-blue-700 mt-0.5">
                            View Payment progress
                          </div>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* Account security — opt-in 2FA settings */}
        <div className="mt-6">
          <TwoFASettingsCard token={token} />
        </div>
      </div>
    </div>
  );
}
